import { Label } from '@/components/shared/Label/Label'
import { Input as FileInput } from '@/components/ui/input'
import { Switch } from '@/components/ui/switch'
import { TabsContent } from '@/components/ui/tabs'
import { CustomizeCheckoutData } from '@/@types/Checkouts/edit-checkout'
import {
  BannerImageField,
  handleBannerFileChange,
} from '@/utils/customizeCheckout'
import { FieldErrors, UseFormClearErrors, UseFormSetValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

type BannersTabProps = {
  values: CustomizeCheckoutData
  setValue: UseFormSetValue<CustomizeCheckoutData>
  clearErrors: UseFormClearErrors<CustomizeCheckoutData>
  errors: FieldErrors<CustomizeCheckoutData>
}

type VisibleField =
  | 'banner_mobile_top_is_visible'
  | 'banner_mobile_bottom_is_visible'
  | 'banner_desktop_top_is_visible'
  | 'banner_desktop_bottom_is_visible'
  | 'banner_desktop_side_is_visible'

function BannerSlot({
  id,
  switchCy,
  inputCy,
  label,
  visibleKey,
  imageKey,
  width,
  height,
  values,
  setValue,
  clearErrors,
  errors,
  onFile,
}: {
  id: string
  switchCy: string
  inputCy: string
  label: string
  visibleKey: VisibleField
  imageKey: BannerImageField
  width: number
  height: number
  values: CustomizeCheckoutData
  setValue: UseFormSetValue<CustomizeCheckoutData>
  clearErrors: UseFormClearErrors<CustomizeCheckoutData>
  errors: FieldErrors<CustomizeCheckoutData>
  onFile: (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: BannerImageField,
  ) => void
}) {
  const { t } = useTranslation(['checkouts'])
  const visible = values[visibleKey] === '1'
  const error = errors[imageKey]?.message

  return (
    <div>
      <div className="flex min-h-11 items-center gap-2">
        <Switch
          id={id}
          checked={visible}
          data-cy={switchCy}
          onCheckedChange={(value) => {
            setValue(visibleKey, value ? '1' : '0')
            if (!value) {
              setValue(imageKey, null)
              clearErrors(imageKey)
            }
          }}
        />
        <Label htmlFor={id}>{label}</Label>
      </div>
      {visible ? (
        <>
          <p className="mb-2 mt-4 text-sm text-muted">
            {t('checkouts:file_recommendations', { size: 4, width, height })}
          </p>
          <FileInput
            type="file"
            accept="image/png, image/jpg, image/jpeg"
            className="text-muted"
            data-cy={inputCy}
            onChange={(event) => onFile(event, imageKey)}
          />
          {error ? (
            <p className="mt-2 text-sm text-error">{String(error)}</p>
          ) : null}
        </>
      ) : null}
    </div>
  )
}

export function BannersTab({
  values,
  setValue,
  clearErrors,
  errors,
}: BannersTabProps) {
  const { t } = useTranslation(['common', 'checkouts'])

  const onFile = (
    event: React.ChangeEvent<HTMLInputElement>,
    fieldName: BannerImageField,
  ) => {
    handleBannerFileChange(event, fieldName, setValue, t, (name) => {
      if (name) clearErrors(name)
    })
  }

  const slotProps = { values, setValue, clearErrors, errors, onFile }

  return (
    <TabsContent value="banners" className="mt-8 space-y-8">
        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-white">
            {t('common:top_banner')}
          </h3>
          <div className="grid gap-6 sm:grid-cols-2">
            <BannerSlot
              {...slotProps}
              id="banner-superior"
              switchCy="switch-banner-desktop-top"
              inputCy="input-banner-desktop-top"
              label={t('checkouts:banner_desktop')}
              visibleKey="banner_desktop_top_is_visible"
              imageKey="banner_desktop_top_image"
              width={1032}
              height={264}
            />
            <BannerSlot
              {...slotProps}
              id="banner-mobile-superior"
              switchCy="switch-banner-mobile-top"
              inputCy="input-banner-mobile-top"
              label={t('checkouts:banner_mobile')}
              visibleKey="banner_mobile_top_is_visible"
              imageKey="banner_mobile_top_image"
              width={343}
              height={120}
            />
          </div>
        </section>

        <section className="space-y-4 border-y border-y-colorBorder py-8">
          <h3 className="text-sm font-semibold text-white">
            {t('common:side_banner')}
          </h3>
          <p className="text-sm text-muted">
            {t('checkouts:banner_side_mobile_absent')}
          </p>
          <BannerSlot
            {...slotProps}
            id="banner-side"
            switchCy="switch-banner-desktop-side"
            inputCy="input-banner-desktop-side"
            label={t('checkouts:banner_desktop')}
            visibleKey="banner_desktop_side_is_visible"
            imageKey="banner_desktop_side_image"
            width={240}
            height={740}
          />
        </section>

        <section className="space-y-4">
          <h3 className="text-sm font-semibold text-white">
            {t('common:bottom_banner')}
          </h3>
          <div className="grid gap-6 sm:grid-cols-2">
            <BannerSlot
              {...slotProps}
              id="banner-inferior"
              switchCy="switch-banner-desktop-bottom"
              inputCy="input-banner-desktop-bottom"
              label={t('checkouts:banner_desktop')}
              visibleKey="banner_desktop_bottom_is_visible"
              imageKey="banner_desktop_bottom_image"
              width={1032}
              height={264}
            />
            <BannerSlot
              {...slotProps}
              id="banner-mobile-inferior"
              switchCy="switch-banner-mobile-bottom"
              inputCy="input-banner-mobile-bottom"
              label={t('checkouts:banner_mobile')}
              visibleKey="banner_mobile_bottom_is_visible"
              imageKey="banner_mobile_bottom_image"
              width={343}
              height={120}
            />
          </div>
        </section>
    </TabsContent>
  )
}
